import { useEffect, useState, type ReactNode } from 'react';
import { getPoolShipping, type ApiClient, type IframePoolShippingDetail } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { isRefundedShipment } from '../../../modules/shippingPromo';
import { SleekButton } from '../../../modules/theme';
import { useWallet } from '../../../modules/wallet';
import { SLEEK_PAGE_MIN_HEIGHT, SleekPageHeader } from '../shared';
import { VaultCardRow } from '../MyPacks/cards/VaultCardRow';
import { ShippingStatusChip } from '../MyPacks/shipping/ShippingStatusChip';
import { ShipSection } from './ShipSection';
import { PaymentSummary } from './PaymentSummary';
import { AddressLinesText } from './AddressLinesText';
import { ShipmentStatusNote } from './ShipmentStatusNote';
import { CancelShipmentSheet } from './CancelShipmentSheet';

type Props = {
  client: ApiClient;
  shippingId: number;
  onBack: () => void;
};

const PAYMENT_NOTE = 'Shipping reflects your selected delivery option. Paid at order from your balance.';
const PRICE_DECIMALS = 2;
const MUTED_CLASS = 'm-0 text-[14px] leading-[20px] text-[var(--co-ShipCard-mutedText,#6D707C)]';

/**
 * One shipment, read-only (partner Figma 2026-10-08 "Shipment Information"):
 * its status, address, delivery type, card and what was paid. A shipment the
 * backend still lets the player cancel keeps its Cancel delivery button.
 */
export function ShipmentInfoPage({ client, shippingId, onBack }: Props) {
  const [shipping, setShipping] = useState<IframePoolShippingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isCancelWindowClosed, setIsCancelWindowClosed] = useState(false);
  const { refresh: refreshWallet } = useWallet();

  useEffect(() => {
    let isStale = false;
    getPoolShipping(client, shippingId)
      .then((res) => !isStale && setShipping(res))
      .catch((err) => !isStale && setError(err instanceof Error ? err.message : 'Failed to load delivery'));
    return () => {
      isStale = true;
    };
  }, [client, shippingId]);

  const page = (body: ReactNode) => (
    <div data-page="shipment-info" data-co-component="ShipCard" className={`@container relative flex ${SLEEK_PAGE_MIN_HEIGHT} w-full flex-col`}>
      <SleekPageHeader onBack={onBack} title="Shipment Information" />
      <main className="mx-auto w-full max-w-[480px] flex-1">{body}</main>
    </div>
  );

  if (!shipping) {
    return page(<p role={error ? 'alert' : undefined} className={`${MUTED_CLASS} px-5`}>{error ?? 'Loading shipment…'}</p>);
  }

  const charge = shipping.chargeBreakdown;
  const insurance = charge?.insurance != null && charge.insurance > 0 ? charge.insurance : null;
  // `shipping` is the full price; the insurance share gets its own row.
  const shippingOnly = insurance !== null && charge?.shipping != null ? Math.round((charge.shipping - insurance) * 100) / 100 : (charge?.shipping ?? null);
  const canCancel = shipping.isAllowToCancel && !isCancelWindowClosed;

  return page(
    <>
      <ShipSection title="Status">
        <span>
          <ShippingStatusChip status={shipping.status} label={shipping.humanizedStatus} settleSummary={shipping.settleSummary} />
        </span>
        <ShipmentStatusNote shipping={shipping} isCancelWindowClosed={isCancelWindowClosed} />
      </ShipSection>
      {shipping.address && (
        <ShipSection title="Delivery Details">
          <AddressLinesText address={shipping.address} />
        </ShipSection>
      )}
      {shipping.service && (
        <ShipSection title="Delivery Type">
          <div className="flex min-h-16 items-center justify-between py-3 gap-4 rounded-[14px] border border-solid border-[var(--co-ShipCard-rowBorder,rgba(255,255,255,0.01))] bg-[var(--co-ShipCard-rowFill,#1A1B1F)] px-4 text-[14px] leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-titleColor,#FFFFFF)]">
            <span className="font-medium">{shipping.service.name}</span>
            {charge?.shipping != null && <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">{formatPrice(charge.shipping, PRICE_DECIMALS)}</span>}
          </div>
        </ShipSection>
      )}
      <ShipSection title={shipping.items.length > 1 ? 'Cards' : 'Card'}>
        {shipping.items.map((item) => (
          <VaultCardRow key={item.id} item={item} />
        ))}
      </ShipSection>
      <ShipSection title="Payment" isLast={!canCancel}>
        <PaymentSummary
          shipping={shippingOnly}
          insurance={insurance}
          showInsurance={insurance !== null}
          handling={charge?.handling ?? null}
          salesTax={charge?.salesTax ?? null}
          total={charge?.total ?? shipping.totalAmount}
          totalLabel="Total paid"
          chargedPromo={charge?.promo}
          refunded={isRefundedShipment(shipping)}
          note={PAYMENT_NOTE}
        />
      </ShipSection>
      {canCancel && (
        <div className="px-5 py-5">
          <SleekButton name="ShippingSecondaryButton" onClick={() => setIsCancelOpen(true)} className="w-full">
            Cancel delivery
          </SleekButton>
        </div>
      )}
      {isCancelOpen && (
        <CancelShipmentSheet
          client={client}
          shippingId={shipping.id}
          onClose={() => setIsCancelOpen(false)}
          onCanceled={(updated) => {
            setShipping(updated);
            setIsCancelOpen(false);
            // The buyback credit and the shipping refund are queued, not sent yet.
            void refreshWallet({ expectCredit: true });
          }}
          onNotCancelable={() => {
            setIsCancelOpen(false);
            setIsCancelWindowClosed(true);
            // Fulfilment started meanwhile — show the status it moved to
            void getPoolShipping(client, shippingId).then(setShipping).catch(() => {});
          }}
        />
      )}
    </>,
  );
}
